import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { getHistoryTasks, deleteHistoryTask } from '../utils/api';
import { HistoryTask } from '../types';
import { useNavigate } from 'react-router-dom';
// 导入国际化钩子
import { useTranslation } from '@/i18n';
// 导入默认图片图标
import DefaultImageIcon from '../components/common/DefaultImageIcon';

const HistoryTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<HistoryTask[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 8; // 每页显示数量
  const navigate = useNavigate();

  // 国际化钩子
  const { t } = useTranslation();

  // 加载历史任务数据 - 使用真实API
  const loadTasks = async () => {
    try {
      setLoading(true);

      // 调用API获取历史任务列表（分页）
      const response = await getHistoryTasks({
        page: currentPage, // 使用当前页
        pageSize: pageSize // 每页显示数量
      });

      // 根据API返回结构解析数据，注意这里可能需要根据实际API返回调整
      // 假设API返回的data格式为 { list: [], total: number, page: number, pageSize: number }
      const taskList = response.data.list || [];
      const total = response.data.total || 0;

      // 计算总页数
      const calculatedTotalPages = total > 0 ? Math.ceil(total / pageSize) : 1;

      // 将API返回的数据转换为前端需要的HistoryTask格式
      // 根据更新的API手册，使用project_id作为唯一标识
      const formattedTasks = taskList.map((task: any) => {
        // 从task.images中提取第一张可用的图片作为封面
        let coverImage: string | null = null; // 默认无封面

        try {
          // 1. 首先尝试直接从task.images获取第一张图片作为封面
          if (task.images && Array.isArray(task.images) && task.images.length > 0) {
            coverImage = task.images[0]; // 使用第一张图片作为封面
          } else {
            // 2. 如果task.images不存在或为空，尝试从project_config中提取
            let projectConfigData: any = {};

            // 处理project_config可能是JSON字符串的情况
            projectConfigData = typeof task.project_config === 'string'
              ? JSON.parse(task.project_config)
              : task.project_config;

            // 兼容新旧两种格式
            let sceneList: any[] = [];
            if (Array.isArray(projectConfigData)) {
              // 旧格式：project_config是直接的场景列表数组
              sceneList = projectConfigData;
            } else if (typeof projectConfigData === 'object' && projectConfigData !== null) {
              // 新格式：project_config是包含bgMusic和sceneList的对象
              sceneList = projectConfigData.sceneList || [];
            }

            // 检查场景列表是否有图片
            if (Array.isArray(sceneList) && sceneList.length > 0) {
              // 遍历场景列表，找到第一张可用的图片
              for (const scene of sceneList) {
                if (scene.images && Array.isArray(scene.images) && scene.images.length > 0) {
                  // 清理图片URL中的空格和反引号
                  coverImage = scene.images[0].trim().replace(/`/g, '');
                  break;
                }
              }
            }
          }
        } catch (error) {
          console.error('获取封面图失败:', error);
        }

        return {
          id: task.project_id, // 使用project_id作为任务的唯一标识
          coverImage,
          title: task.title, // 恢复使用task.title作为标题
          createdAt: task.created_at,
          status: 'completed' as const // 由于API未返回状态，暂时默认所有任务为已完成状态
        };
      });

      setTasks(formattedTasks);
      setTotalPages(calculatedTotalPages);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || t('history.tasks.loadError'));
      setLoading(false);
    }
  };

  // 删除任务 - 使用真实API
  const handleDeleteTask = async (taskId: string) => {
    try {
      // 调用API删除任务
      await deleteHistoryTask(taskId);

      // 重新加载任务列表
      loadTasks();
    } catch (err: any) {
      setError(err.message || t('history.tasks.delete.error'));
    }
  };

  // 点击任务项，导航到视频编辑页面
  const handleTaskClick = (taskId: string) => {
    navigate(`/production/video/edit?projectId=${taskId}`);
  };

  // 当页码变化时重新加载数据
  useEffect(() => {
    loadTasks();
  }, [currentPage]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center py-20">
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center py-20">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 h-screen flex flex-col overflow-hidden">
      <div className="mb-6 flex justify-between items-center">
        <span className="text-sm text-gray-400">{t('history.tasks.total', { count: tasks.length })}</span>
      </div>

      {/* 主要内容区域 - 包含任务列表和空状态，支持垂直滚动 */}
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar" style={{ flex: '0.95' }}>
        {/* 任务列表 - 4列网格布局 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tasks.map((task) => (
            <div key={task.id} className="bg-gray-900 rounded-xl overflow-hidden shadow-lg border border-gray-800 hover:border-blue-500 transition-all duration-200 relative">
              {/* 封面图片 - 3:4 比例 */}
              <div className="relative aspect-[3/4] bg-gray-800/30 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                {/* 条件渲染：有封面图则显示图片，否则显示默认图标 */}
                {task.coverImage ? (
                  <img
                    src={task.coverImage}
                    alt={task.title}
                    className="w-full h-full object-cover"
                    onClick={() => handleTaskClick(task.id)}
                  />
                ) : (
                  <DefaultImageIcon className="w-full h-full" />
                )}
                {/* 删除按钮 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止冒泡，避免触发任务项点击
                    handleDeleteTask(task.id);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/45 rounded-full text-white/80 hover:bg-red-500/80 hover:text-white transition-opacity duration-300"
                >
                  <Trash2 size={14} />
                </button>

                {/* 内容区域 - 绝对定位在图片底部 */}
                <div className="absolute bottom-0 left-0 right-0 py-2 px-4 cursor-pointer bg-gradient-to-t from-gray-900/90 via-gray-900/70 to-transparent backdrop-blur-sm hover:bg-gradient-to-t from-gray-800/90 via-gray-800/70 to-transparent transition-all" onClick={() => handleTaskClick(task.id)}>
                  {/* 标题 */}
                  <h4 className="text-sm font-semibold text-white mb-2 line-clamp-2">{task.title}</h4>

                  {/* 创建时间 */}
                  <div className="text-xs text-gray-400 mb-2">
                    {new Date(task.createdAt).toLocaleString('zh-CN')}
                  </div>


                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 没有任务时的提示 */}
        {
          tasks.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400">{t('history.tasks.empty')}</p>
            </div>
          )
        }
      </div>

      {/* 分页组件 - 始终在底部显示 */}
      <div className="py-4">
        <div className="flex justify-center items-center mb-2 gap-4">
          {tasks.length > 0 && totalPages > 1 && (
            <>
              {/* 上一页 */}
              <button
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(prev => prev - 1);
                  }
                }}
                disabled={currentPage <= 1}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage <= 1
                  ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed border border-gray-700/50'
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 border border-blue-600/40'
                  }`}
              >
                &lt;
              </button>

              {/* 页码显示 */}
              <div className="text-gray-400 text-sm whitespace-nowrap">
                {currentPage} / {totalPages}
              </div>

              {/* 下一页 */}
              <button
                onClick={() => {
                  if (currentPage < totalPages) {
                    setCurrentPage(prev => prev + 1);
                  }
                }}
                disabled={currentPage >= totalPages}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage >= totalPages
                  ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed border border-gray-700/50'
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 border border-blue-600/40'
                  }`}
              >
                &gt;
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default HistoryTasksPage